import * as React from "react";
import { Drawer, useMediaQuery, useTheme } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PulseChat from "./PulseChat";

export const PulseContext = React.createContext({
  openPulse: () => {},
  closePulse: () => {},
  isOpen: false,
});

export const usePulse = () => React.useContext(PulseContext);

const PulseDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  return (
    <Drawer
      anchor={isRtl ? "left" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 420,
          maxWidth: "100vw",
          height: "100%",
          background: "var(--card-solid)",
          backgroundImage: "none",
          color: "var(--textPrimary)",
          borderInlineStart: "1px solid var(--borderColor)",
        },
      }}
    >
      {open && <PulseChat variant="drawer" onClose={onClose} />}
    </Drawer>
  );
};

function PulseProviderInner({ children, user }) {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const onPulsePage = location.pathname === "/pulse";
  const hideFab = onPulsePage || location.pathname === "/login";
  const loggedIn = Boolean(user);

  React.useEffect(() => {
    if (onPulsePage) setOpen(false);
  }, [onPulsePage]);

  const value = React.useMemo(
    () => ({
      openPulse: () => {
        if (location.pathname !== "/pulse") setOpen(true);
      },
      closePulse: () => setOpen(false),
      isOpen: open,
    }),
    [open, location.pathname]
  );

  return (
    <PulseContext.Provider value={value}>
      {children}
      {loggedIn && (
        <>
          {!hideFab && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t("pulse.title")}
              style={{
                position: "fixed",
                bottom: 24,
                insetInlineEnd: 20,
                zIndex: 1200,
                width: 56,
                height: 56,
                borderRadius: 18,
                border: "1px solid rgba(190,50,62,0.45)",
                background: "linear-gradient(160deg, #9c0410, #4a0107)",
                color: "#ffeaea",
                boxShadow: "0 12px 28px rgba(125,1,12,0.55)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
            >
              <AutoAwesome />
            </button>
          )}
          <PulseDrawer open={open} onClose={() => setOpen(false)} />
        </>
      )}
    </PulseContext.Provider>
  );
}

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
});

export const PulseProvider = connect(mapStateToProps)(PulseProviderInner);

export default PulseProvider;
